/**
 * TechWorkCenter · /tecnico/orden/:orderId/centro-trabajo
 * Centro de Trabajo del técnico con 3 botones funcionales + bloqueo de cierre.
 * Estilo neumórfico CLARO de alto contraste.
 *
 * v4.0.0 — Fase 3
 */
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  ArrowLeft, Package, Wifi, ClipboardCheck, Lock, Unlock,
  CheckCircle2, XCircle, AlertTriangle, Loader2, RefreshCw,
  Clock, User, MapPin, Zap, ChevronRight, Camera, Wrench,
} from "lucide-react";

/* ═══════════════════════ Paleta LIGHT ═══════════════════════ */
const BG = "#EEF1F5";
const CARD = "#FFFFFF";
const INSET = "#E2E6EC";
const TEXT = "#0A1628";
const TEXT2 = "#4B5C6B";
const ACCENT = "#0A3747";
const ACCENT2 = "#E0E621";
const GREEN = "#059669";
const RED = "#DC2626";
const AMBER = "#D97706";
const BLUE = "#2563EB";

const neuCard = {
  background: CARD,
  borderRadius: 20,
  boxShadow: "6px 6px 14px rgba(163,177,198,0.35), -6px -6px 14px rgba(255,255,255,0.9)",
  border: "1px solid rgba(255,255,255,0.8)",
};
const neuInset = {
  background: INSET,
  borderRadius: 16,
  boxShadow: "inset 3px 3px 6px rgba(163,177,198,0.4), inset -3px -3px 6px rgba(255,255,255,0.7)",
};

/* ═══════════════════════ Sub-componentes ═══════════════════════ */

function ActionButton({ icon: Icon, label, sublabel, color, onClick, disabled, testId }) {
  return (
    <motion.button
      whileHover={disabled ? {} : { y: -2, scale: 1.02 }}
      whileTap={disabled ? {} : { scale: 0.97 }}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className="w-full text-left p-5 transition-all"
      style={{
        ...neuCard,
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
      data-testid={testId}
    >
      <div className="flex items-center gap-4">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
          style={{
            background: `${color}12`,
            boxShadow: `inset -2px -2px 5px rgba(255,255,255,0.9), inset 2px 2px 5px ${color}18`,
          }}
        >
          <Icon size={26} style={{ color }} strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[15px] font-bold" style={{ color: TEXT }}>{label}</p>
          <p className="text-[12px] mt-0.5" style={{ color: TEXT2 }}>{sublabel}</p>
        </div>
        <ChevronRight size={20} style={{ color: TEXT2 }} />
      </div>
    </motion.button>
  );
}

function ReadinessCheck({ label, ok, detail }) {
  return (
    <div className="flex items-center gap-3 py-2">
      <div
        className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
        style={{
          background: ok ? `${GREEN}15` : `${RED}15`,
          boxShadow: `inset 1px 1px 3px rgba(0,0,0,0.06)`,
        }}
      >
        {ok
          ? <CheckCircle2 size={16} style={{ color: GREEN }} />
          : <XCircle size={16} style={{ color: RED }} />
        }
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium" style={{ color: ok ? GREEN : TEXT }}>{label}</p>
        {detail && <p className="text-[11px]" style={{ color: TEXT2 }}>{detail}</p>}
      </div>
    </div>
  );
}

/* ═══════════════════════ Página principal ═══════════════════════ */
export default function TechWorkCenter() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: wc } = await api.get(`/v1/mobile/orders/${orderId}/work-center`);
      setData(wc);
    } catch (e) {
      console.error("WorkCenter load error:", e);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleClose = async () => {
    if (!data?.close_readiness?.can_close) return;
    setClosing(true);
    try {
      await api.post(`/v1/mobile/orders/${orderId}/close`, {
        location: null, notes: "Cierre desde Centro de Trabajo",
      });
      navigate("/tecnico/mis-ordenes");
    } catch (e) {
      console.error("Close error:", e);
      alert(e?.response?.data?.detail || "Error al cerrar la OT");
    } finally {
      setClosing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: BG }}>
        <Loader2 size={36} className="animate-spin" style={{ color: ACCENT }} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6" style={{ background: BG }}>
        <AlertTriangle size={48} style={{ color: AMBER }} />
        <p className="mt-4 text-base font-semibold" style={{ color: TEXT }}>Orden no encontrada</p>
        <button
          onClick={() => navigate("/tecnico/mis-ordenes")}
          className="mt-4 px-6 py-2 rounded-xl text-sm font-semibold"
          style={{ ...neuCard, color: ACCENT }}
        >
          Volver
        </button>
      </div>
    );
  }

  const cr = data.close_readiness || {};
  const canClose = cr.can_close;
  const missing = cr.missing || [];

  return (
    <div className="min-h-screen pb-32" style={{ background: BG }} data-testid="tech-work-center">
      {/* Header */}
      <div
        className="sticky top-0 z-50 px-4 py-3"
        style={{ background: `${BG}ee`, backdropFilter: "blur(12px)" }}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/tecnico/mis-ordenes")}
            className="p-2 rounded-xl"
            style={neuCard}
            data-testid="wc-back-btn"
          >
            <ArrowLeft size={18} style={{ color: ACCENT }} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold truncate" style={{ color: TEXT }}>
              Centro de Trabajo
            </h1>
            <p className="text-xs truncate" style={{ color: TEXT2 }}>
              OT-{data.odoo_task_id} · {data.client_name || "—"}
            </p>
          </div>
          <button
            onClick={fetchData}
            className="p-2 rounded-xl"
            style={neuCard}
            data-testid="wc-refresh-btn"
          >
            <RefreshCw size={16} style={{ color: ACCENT }} />
          </button>
        </div>
      </div>

      <div className="px-4 pt-3 space-y-4 max-w-lg mx-auto">
        {/* Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4"
          style={neuCard}
        >
          <div className="flex items-center gap-2 mb-2">
            <span
              className="text-[11px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider"
              style={{ background: `${BLUE}12`, color: BLUE }}
            >
              {data.status === "in_progress" ? "En Ejecución" : data.status || "—"}
            </span>
            {data.scheduled_at && (
              <span className="flex items-center gap-1 text-[11px]" style={{ color: TEXT2 }}>
                <Clock size={11} /> {new Date(data.scheduled_at).toLocaleTimeString("es-EC", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "America/Guayaquil" })}
              </span>
            )}
          </div>
          {data.plan_name && (
            <p className="text-[13px] font-semibold mb-1" style={{ color: ACCENT }}>
              {data.plan_name}
            </p>
          )}
          {/* Resumen IA */}
          <div className="mt-2 p-3 rounded-xl" style={neuInset}>
            <p className="text-[11px] font-semibold mb-1 uppercase tracking-wider" style={{ color: TEXT2 }}>
              Resumen
            </p>
            <p className="text-[12px] leading-relaxed" style={{ color: TEXT }}>
              {data.ia_summary || "Sin información disponible."}
            </p>
          </div>
        </motion.div>

        {/* 3 Action Buttons */}
        <div className="space-y-3">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
            <ActionButton
              icon={Package}
              label="Materiales (Receta)"
              sublabel={data.materials?.registered
                ? `${data.materials.count} material(es) registrado(s)`
                : "Registrar en autofiscalización"
              }
              color={data.materials?.registered ? GREEN : AMBER}
              onClick={() => navigate(`/tecnico/orden/${orderId}/materiales`)}
              testId="wc-btn-materials"
            />
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
            <ActionButton
              icon={Wifi}
              label="Autoaprovisionar"
              sublabel={data.provisioning?.cpe_supply_completed
                ? `CPE: ${data.provisioning.cpe_serial || "OK"}`
                : "Aprovisionamiento CPE pendiente"
              }
              color={data.provisioning?.cpe_supply_completed ? GREEN : BLUE}
              onClick={() => navigate(`/tecnico/orden/${orderId}/provisioning`)}
              testId="wc-btn-provisioning"
            />
          </motion.div>

          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}>
            <ActionButton
              icon={ClipboardCheck}
              label="Autofiscalización"
              sublabel={
                data.fiscalization?.form_complete
                  ? "Formulario completado"
                  : data.fiscalization?.photos_ok
                    ? `${data.fiscalization.photos_completed}/${data.fiscalization.photos_required} fotos · Pendiente finalizar`
                    : `${data.fiscalization?.photos_completed || 0}/${data.fiscalization?.photos_required || "?"} fotos requeridas`
              }
              color={data.fiscalization?.form_complete ? GREEN : data.fiscalization?.photos_ok ? AMBER : RED}
              onClick={() => navigate(`/tecnico/orden/${orderId}/fiscalizacion`)}
              testId="wc-btn-fiscalization"
            />
          </motion.div>
        </div>

        {/* Close Readiness */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="p-5"
          style={neuCard}
        >
          <div className="flex items-center gap-2 mb-3">
            {canClose
              ? <Unlock size={18} style={{ color: GREEN }} />
              : <Lock size={18} style={{ color: RED }} />
            }
            <p className="text-[14px] font-bold" style={{ color: TEXT }}>
              {canClose ? "OT lista para cerrar" : "Requisitos para cierre"}
            </p>
          </div>

          <div className="space-y-0.5">
            <ReadinessCheck
              label="Aprovisionamiento CPE"
              ok={cr.cpe_supply_completed}
              detail={data.provisioning?.cpe_serial ? `Serial: ${data.provisioning.cpe_serial}` : null}
            />
            <ReadinessCheck
              label="Materiales registrados"
              ok={cr.materials_registered}
              detail={data.materials?.count > 0 ? `${data.materials.count} registrado(s) en autofiscalización` : null}
            />
            <ReadinessCheck
              label={`Fotos de autofiscalización (${data.fiscalization?.photos_completed || 0}/${data.fiscalization?.photos_required || "?"})`}
              ok={cr.photos_ok}
            />
          </div>

          {missing.length > 0 && (
            <div className="mt-3 p-3 rounded-xl" style={{ ...neuInset, background: `${RED}06` }}>
              <p className="text-[11px] font-semibold mb-1" style={{ color: RED }}>Pendiente:</p>
              {missing.map((m, i) => (
                <p key={i} className="text-[11px] flex items-center gap-1.5" style={{ color: TEXT2 }}>
                  <AlertTriangle size={10} style={{ color: AMBER }} /> {m}
                </p>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Fixed bottom: Finalizar OT */}
      <div
        className="fixed bottom-0 left-0 right-0 p-4 z-50"
        style={{ background: `${BG}ee`, backdropFilter: "blur(12px)" }}
      >
        <div className="max-w-lg mx-auto">
          <motion.button
            whileHover={canClose ? { scale: 1.02 } : {}}
            whileTap={canClose ? { scale: 0.97 } : {}}
            onClick={handleClose}
            disabled={!canClose || closing}
            className="w-full py-4 rounded-2xl text-[15px] font-bold tracking-wide flex items-center justify-center gap-2 transition-all"
            style={{
              background: canClose
                ? `linear-gradient(135deg, ${ACCENT}, #0E4D5E)`
                : INSET,
              color: canClose ? "#FFFFFF" : TEXT2,
              boxShadow: canClose
                ? `6px 6px 14px rgba(10,55,71,0.3), -4px -4px 10px rgba(255,255,255,0.6)`
                : `inset 3px 3px 6px rgba(163,177,198,0.4), inset -3px -3px 6px rgba(255,255,255,0.7)`,
              cursor: canClose ? "pointer" : "not-allowed",
            }}
            data-testid="wc-btn-close"
          >
            {closing ? (
              <Loader2 size={20} className="animate-spin" />
            ) : canClose ? (
              <CheckCircle2 size={20} />
            ) : (
              <Lock size={18} />
            )}
            {closing ? "Cerrando..." : canClose ? "Finalizar OT" : "Finalizar OT (bloqueado)"}
          </motion.button>
        </div>
      </div>
    </div>
  );
}
